import { CdkTrapFocus } from '@angular/cdk/a11y';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  runInInjectionContext,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  EMPTY_SEARCH_RESULTS,
  RESULT_GROUPS,
  SEARCH_PLACEHOLDER
} from '@components/global-search/global-search.constants';
import { SearchEmptyStateComponent } from '@components/global-search/search-empty-state/search-empty-state.component';
import { SearchFooterHintsComponent } from '@components/global-search/search-footer-hints/search-footer-hints.component';
import { SearchInputRowComponent } from '@components/global-search/search-input-row/search-input-row.component';
import { SearchNoResultsComponent } from '@components/global-search/search-no-results/search-no-results.component';
import { SearchResultRowComponent } from '@components/global-search/search-result-row/search-result-row.component';
import { IconComponent } from '@components/icon/icon.component';
import { SpinnerComponent } from '@components/spinner/spinner.component';
import {
  SearchResult,
  SearchResultsGroup,
  SearchStatus,
  SearchSuggestion
} from '@interfaces/search-result.interface';
import { AnalyticsService } from '@services/analytics/analytics.service';
import { SearchService } from '@services/search/search.service';
import { SearchUiService } from '@services/search/search-ui.service';
import {
  flattenSearchResults,
  globalSearchResultIndex,
  isSearchResultActive,
  nextActiveSearchIndex,
  SearchResultGroupKey
} from '@shared/utils/search-keyboard-nav.util';
import { firstValueFrom, Subject } from 'rxjs';

@Component({
  selector: 'app-global-search-overlay',
  imports: [
    CdkTrapFocus,
    IconComponent,
    SearchEmptyStateComponent,
    SearchFooterHintsComponent,
    SearchInputRowComponent,
    SearchNoResultsComponent,
    SearchResultRowComponent,
    SpinnerComponent
  ],
  templateUrl: './global-search-overlay.component.html',
  styleUrl: './global-search-overlay.component.scss'
})
export class GlobalSearchOverlayComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly searchUiService = inject(SearchUiService);
  private readonly querySubject = new Subject<string>();
  private readonly searchInputRow = viewChild(SearchInputRowComponent);
  private readonly resultsScroll = viewChild<ElementRef<HTMLElement>>('resultsScroll');
  public readonly isOpen = this.searchUiService.isOpen;
  public readonly query = signal('');
  public readonly status = signal<SearchStatus>('empty');
  public readonly results = signal<SearchResultsGroup>(EMPTY_SEARCH_RESULTS);
  public readonly activeIndex = signal(-1);
  public readonly recent = signal<string[]>([]);
  public readonly suggestions = signal<SearchSuggestion[]>([]);
  public readonly isDesktop = signal(this.getIsDesktop());
  public readonly resultGroups = RESULT_GROUPS;
  public readonly searchPlaceholder = SEARCH_PLACEHOLDER;
  public readonly flattenedResults = computed(() => flattenSearchResults(this.results()));
  public readonly totalCount = computed(() => this.flattenedResults().length);

  constructor() {
    this.setupSearchPipeline();

    effect(() => {
      if (this.isOpen()) {
        document.body.style.overflow = 'hidden';
        void this.loadOpenData();
        runInInjectionContext(this.injector, () => {
          afterNextRender(() => this.searchInputRow()?.focus());
        });
      } else {
        this.onClose();
      }
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
      this.destroyRef.onDestroy(() => {
        window.removeEventListener('resize', this.handleResize);
        document.body.style.overflow = '';
      });
    }
  }

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen()) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close('escape');
      return;
    }

    if (!this.isDesktop()) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveActive(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActive(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.selectActiveResult();
    }
  }

  public onQueryChange(value: string): void {
    this.query.set(value);
    const trimmed = value.trim();
    if (!trimmed) {
      this.status.set('empty');
      this.results.set(EMPTY_SEARCH_RESULTS);
      this.activeIndex.set(-1);
      // Reset pipeline so re-selecting the same recent term is not skipped by distinctUntilChanged
      this.querySubject.next('');
      return;
    }
    this.status.set('loading');
    this.querySubject.next(value);
  }

  public clearQuery(): void {
    this.analyticsService.trackEvent('global_search', 'clear_query');
    this.query.set('');
    this.status.set('empty');
    this.results.set(EMPTY_SEARCH_RESULTS);
    this.activeIndex.set(-1);
    this.querySubject.next('');
    this.searchInputRow()?.focus();
  }

  public close(reason: 'escape' | 'scrim' | 'back' | 'navigation' = 'back'): void {
    this.analyticsService.trackEvent('global_search', 'close', reason);
    this.searchUiService.close();
    this.resetState();
  }

  public onScrimClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('search-scrim')) {
      this.close('scrim');
    }
  }

  public onRecentClick(term: string): void {
    this.analyticsService.trackEvent('global_search', 'recent_select');
    this.query.set(term);
    this.status.set('loading');
    this.querySubject.next(term);
    this.searchInputRow()?.focus();
  }

  public async onSuggestionClick(suggestion: SearchSuggestion): Promise<void> {
    this.analyticsService.trackEvent('global_search', 'suggestion_select', suggestion.label);
    this.close('navigation');
    await this.router.navigate(suggestion.routerLink);
  }

  public async onResultSelect(result: SearchResult, inputMethod: 'click' | 'keyboard' = 'click'): Promise<void> {
    const positionIndex = this.flattenedResults().findIndex(item => item.id === result.id && item.type === result.type);
    this.analyticsService.trackEvent(
      'global_search',
      'result_select',
      `${result.type}:${inputMethod}`,
      positionIndex >= 0 ? positionIndex : undefined
    );
    await this.searchService.saveRecentSearch(this.query());
    this.close('navigation');
    await this.router.navigate(result.routerLink);
  }

  public globalIndex(group: SearchResultGroupKey, localIndex: number): number {
    return globalSearchResultIndex(this.results(), group, localIndex);
  }

  public isActive(group: SearchResultGroupKey, localIndex: number): boolean {
    return isSearchResultActive(this.activeIndex(), this.results(), group, localIndex);
  }

  public getGroupItems(group: SearchResultGroupKey): SearchResult[] {
    return this.results()[group];
  }

  private readonly handleResize = (): void => {
    this.isDesktop.set(this.getIsDesktop());
  };

  private getIsDesktop(): boolean {
    return typeof window !== 'undefined' && window.innerWidth >= 1200;
  }

  private setupSearchPipeline(): void {
    this.searchService
      .search(this.querySubject.asObservable())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(searchResults => {
        if (!this.query().trim()) {
          return;
        }
        const count = flattenSearchResults(searchResults).length;
        this.results.set(searchResults);
        this.status.set(count > 0 ? 'results' : 'no-results');
        this.activeIndex.set(count > 0 ? 0 : -1);
        this.analyticsService.trackSiteSearch(this.query(), count);
        this.analyticsService.trackEvent(
          'global_search',
          'query_complete',
          count > 0 ? 'has_results' : 'no_results',
          count
        );
      });
  }

  private async loadOpenData(): Promise<void> {
    this.recent.set(await this.searchService.getRecentSearches());
    this.suggestions.set(await firstValueFrom(this.searchService.suggestions$));
  }

  private onClose(): void {
    document.body.style.overflow = '';
  }

  private resetState(): void {
    this.query.set('');
    this.status.set('empty');
    this.results.set(EMPTY_SEARCH_RESULTS);
    this.activeIndex.set(-1);
    this.querySubject.next('');
  }

  private moveActive(delta: number): void {
    const items = this.flattenedResults();
    const next = nextActiveSearchIndex(this.activeIndex(), items.length, delta);
    if (next === -1) {
      return;
    }
    this.activeIndex.set(next);
    this.scrollActiveIntoView();
  }

  private selectActiveResult(): void {
    const items = this.flattenedResults();
    if (items.length === 0) {
      return;
    }
    const index = this.activeIndex() < 0 ? 0 : this.activeIndex();
    void this.onResultSelect(items[index], 'keyboard');
  }

  private scrollActiveIntoView(): void {
    const scrollContainer = this.resultsScroll()?.nativeElement;
    if (!scrollContainer) {
      return;
    }
    const activeRow = scrollContainer.querySelector('[data-active="true"]');
    activeRow?.scrollIntoView({ block: 'nearest' });
  }
}
